import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "next-i18next";

interface GoogleOAuthButtonProps {
  onSuccess: (idToken: string) => void;
  isLoading?: boolean;
  text?: string;
}

declare global {
  interface Window {
    google?: any;
  }
}

export default function GoogleOAuthButton({
  onSuccess,
  isLoading = false,
  text,
}: GoogleOAuthButtonProps) {
  const { t } = useTranslation("common-page");
  const buttonContainerRef = useRef<HTMLDivElement>(null);

  const googleClientId =
    process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ||
    "386111737297-gpnbb7dp92o1h8hd5t8tj5h2m0gf3dv2.apps.googleusercontent.com";

  useEffect(() => {
    let isMounted = true;

    const handleCredentialResponse = (response: any) => {
      if (response?.credential) {
        onSuccess(response.credential);
      }
    };

    const renderGoogleButton = () => {
      if (window.google?.accounts?.id && buttonContainerRef.current) {
        try {
          window.google.accounts.id.initialize({
            client_id: googleClientId,
            callback: handleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: true,
          });

          buttonContainerRef.current.innerHTML = "";
          window.google.accounts.id.renderButton(buttonContainerRef.current, {
            theme: "outline",
            size: "large",
            type: "standard",
            text: "continue_with",
            shape: "rectangular",
            logo_alignment: "left",
            width: "400",
          });
        } catch (err) {
          console.error("Error initializing Google SDK:", err);
        }
      }
    };

    if (window.google?.accounts?.id) {
      renderGoogleButton();
    } else {
      const existingScript = document.getElementById("google-gsi-script");
      if (!existingScript) {
        const script = document.createElement("script");
        script.id = "google-gsi-script";
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = () => {
          if (isMounted) renderGoogleButton();
        };
        document.body.appendChild(script);
      } else {
        existingScript.addEventListener("load", renderGoogleButton);
        const timer = setTimeout(() => {
          if (isMounted) renderGoogleButton();
        }, 500);
        return () => clearTimeout(timer);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [googleClientId, onSuccess]);

  const handleCustomButtonClick = () => {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.prompt();
    }
  };

  return (
    <div className="w-full relative group min-h-[44px]">
      {/* Custom Full-Length Styled Button UI */}
      <button
        type="button"
        onClick={handleCustomButtonClick}
        disabled={isLoading}
        className="w-full h-11 px-4 border border-border/80 bg-background/50 hover:bg-background/80 hover:border-primary/50 text-foreground font-semibold rounded-lg flex items-center justify-center gap-3 transition-all duration-200 shadow-sm active:scale-[0.99] cursor-pointer"
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
            />
          </svg>
        )}
        <span className="text-sm font-semibold tracking-wide">
          {text || t("Continue with Google")}
        </span>
      </button>

      {/* Official GIS Render Container Overlay - Unconditionally present in DOM */}
      <div
        ref={buttonContainerRef}
        className={`absolute inset-0 z-10 w-full h-full cursor-pointer overflow-hidden ${
          isLoading
            ? "pointer-events-none opacity-0"
            : "opacity-[0.001] [&>div]:!w-full [&>div]:!h-full [&_iframe]:!w-full [&_iframe]:!h-full [&_iframe]:!scale-150"
        }`}
      />
    </div>
  );
}
